import React, { useState } from 'react';
import { FaBell, FaClipboardCheck, FaIndustry } from 'react-icons/fa';
import css from './NotificationSettings.module.css';

// List of basins and crews
const basinsWithCrews = [
  {
    id: 'dj',
    name: 'DJ',
    crews: ['Anthem', 'Constitution', 'Freedom', 'Independence', 'Patriot', 'Republic']
  },
  {
    id: 'westtexas',
    name: 'West Texas',
    crews: ['Alliance', 'Browning', 'Dynasty', 'Eclipse', 'Legacy', 'Lonestar', 'Olympus', 'Phantom', 'Remington', 'Barrett', 'Sabre', 'Spartan', 'Titan', 'Vapor']
  },
  {
    id: 'canada',
    name: 'Canada', 
    crews: ['Honey Badgers', 'Ironside', 'Northern Thunder', 'Rebels']
  },
  {
    id: 'southtexas',
    name: 'South Texas',
    crews: ['Apache', 'Kiowa', 'Raptor', 'Spectre']
  },
  {
    id: 'powderriver',
    name: 'Powder River',
    crews: ['Justice', 'Revolution']
  },
  {
    id: 'haynesville',
    name: 'Haynesville',
    crews: ['Cobra', 'Easy Company', 'Falcon', 'Nighthawk', 'Reaper', 'Spitfire']
  }
];

// List of post job metrics
const postJobMetrics = [
  { id: 'max_rate', label: 'Max Rate' },
  { id: 'max_pressure', label: 'Max Pressure' },
  { id: 'avg_rate', label: 'Average Rate' },
  { id: 'avg_pressure', label: 'Average Pressure' },
  { id: 'chem_gallons', label: 'Chemical Gallons Used' },
  { id: 'proppant_used', label: 'Proppant Used' },
  { id: 'pump_time', label: 'Pump Time' },
  { id: 'downtime', label: 'Downtime' },
  { id: 'and_more', label: 'And More...' }
];

const NotificationSettings: React.FC = () => {
  const [selectedBasins, setSelectedBasins] = useState<string[]>([]);
  const [selectedCrews, setSelectedCrews] = useState<string[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);

  const toggleBasin = (basinId: string) => {
    // This would normally update the state but is disabled due to "Coming Soon"
    console.log(`Toggle basin: ${basinId}`);
  };

  const toggleCrew = (crewName: string) => {
    // This would normally update the state but is disabled due to "Coming Soon"
    console.log(`Toggle crew: ${crewName}`);
  };

  const toggleMetric = (metricId: string) => {
    // This would normally update the state but is disabled due to "Coming Soon"
    console.log(`Toggle metric: ${metricId}`);
  };
  
  const isCrewEnabled = (basinId: string) => {
    return selectedBasins.includes(basinId);
  };

  return (
    <div className={css.notificationSettings}>
      <div className={css.comingSoonOverlay}>
        <div className={css.comingSoonBanner}>
          <span>Coming Soon!</span>
        </div>
      </div>

      <div className={css.section}>
        <h2 className={css.sectionTitle}>
          <FaBell className={css.sectionIcon} />
          Email Notification Settings
        </h2>
        <p className={css.sectionDescription}>
          Customize which notifications you receive via email
        </p>
      </div>
      
      <div className={css.section}>
        <h3 className={css.subsectionTitle}>
          <FaClipboardCheck className={css.subsectionIcon} />
          Post Job Email Reports
        </h3>
        <div className={css.metricsContainer}>
          <p className={css.metricsDescription}>
            Select which metrics to include in post job email reports:
          </p>
          
          <div className={css.checkboxGrid}>
            {postJobMetrics.map(metric => (
              <div key={metric.id} className={css.checkboxItem}>
                <label className={css.checkboxLabel}>
                  <input 
                    type="checkbox"
                    checked={selectedMetrics.includes(metric.id)}
                    onChange={() => toggleMetric(metric.id)}
                    className={css.checkbox}
                    disabled
                  />
                  <span className={css.checkboxText}>{metric.label}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className={css.section}>
        <h3 className={css.subsectionTitle}>
          <FaIndustry className={css.subsectionIcon} />
          Basin & Crew Selection
        </h3>
        <div className={css.basinContainer}>
          <p className={css.basinDescription}>
            Select which basins and crews you want to receive notifications for:
          </p>
          
          <div className={css.basinGrid}>
            {basinsWithCrews.map((basin) => (
              <div key={basin.id} className={css.basinItem}>
                <div className={css.basinHeader}>
                  <label className={css.basinLabel}>
                    <input 
                      type="checkbox"
                      checked={selectedBasins.includes(basin.id)}
                      onChange={() => toggleBasin(basin.id)}
                      className={css.checkbox}
                      disabled
                    />
                    <span className={css.basinName}>{basin.name}</span>
                  </label>
                </div>
                
                <div className={css.crewList}>
                  {basin.crews.map((crew) => (
                    <div key={crew} className={css.crewItem}>
                      <label className={`${css.crewLabel} ${!isCrewEnabled(basin.id) ? css.disabled : ''}`}>
                        <input 
                          type="checkbox"
                          checked={selectedCrews.includes(crew)}
                          onChange={() => toggleCrew(crew)}
                          disabled={!isCrewEnabled(basin.id) || true}
                          className={css.checkbox}
                        />
                        <span className={css.crewName}>{crew}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <button className={css.saveButton} disabled>
        Save Notification Preferences
      </button>
    </div>
  );
};

export default NotificationSettings;